import { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";

const inputClass =
  "w-full border border-slate-300 px-3 py-2.5 text-sm rounded-sm focus:border-slate-900 focus:outline-none bg-white text-slate-900";

export default function PasswordReset() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const c = t.credit;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const requestLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/participant/forgot-password", { email });
      setSent(true);
    } catch {
      setSent(true); // never reveal enumeration
    } finally {
      setLoading(false);
    }
  };

  const setNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/participant/reset-password", { token, password });
      setDone(true);
    } catch {
      setError(c.resetInvalid);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf8f5] text-slate-900 flex flex-col" data-testid="password-reset-page">
      <div className="border-b spec-border bg-white p-6">
        <Link to="/" className="flex items-center w-fit">
          <Logo markClass="w-6 h-6 text-blue-600" textClass="text-lg" />
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-sm bg-white border spec-border p-8 rounded-2xl shadow-sm">
          {done ? (
            <div className="text-center" data-testid="reset-done">
              <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={40} />
              <h1 className="text-xl font-bold text-slate-950">{c.resetSuccess}</h1>
              <p className="text-sm text-slate-600 mt-2">{c.resetSuccessSub}</p>
              <button
                data-testid="reset-to-login"
                onClick={() => navigate("/konto/login")}
                className="inline-block mt-6 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-mono-spec font-bold rounded-full uppercase tracking-wider transition-colors"
              >
                {c.resetToLogin}
              </button>
            </div>
          ) : token ? (
            <>
              <h1 className="text-xl font-bold text-slate-950">{c.resetNewTitle}</h1>
              <p className="text-xs text-slate-500 mt-1 mb-6 font-mono-spec">{c.resetNewSub}</p>
              <form onSubmit={setNewPassword} className="space-y-4" data-testid="reset-new-form">
                <div>
                  <label className="block text-xs font-mono-spec text-slate-700 mb-1">{c.resetNewPassword}</label>
                  <input
                    data-testid="reset-new-password"
                    type="password"
                    className={inputClass}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
                {error && <p className="text-xs text-red-500 font-mono-spec" data-testid="reset-error"><XCircle className="inline mr-1" size={12} />{error}</p>}
                <button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={loading || password.length < 8}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-mono-spec text-xs font-bold rounded-full uppercase tracking-wider transition-colors disabled:opacity-40"
                >
                  {c.resetSet}
                </button>
              </form>
            </>
          ) : sent ? (
            <div className="text-center" data-testid="reset-sent">
              <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={40} />
              <p className="text-sm text-slate-700">{c.resetSent}</p>
              <Link to="/konto/login" className="inline-block mt-6 text-xs font-mono-spec text-blue-700 hover:text-blue-900">{c.resetToLogin}</Link>
            </div>
          ) : (
            <>
              <h1 className="text-xl font-bold text-slate-950">{c.resetReqTitle}</h1>
              <p className="text-xs text-slate-500 mt-1 mb-6 font-mono-spec">{c.resetReqSub}</p>
              <form onSubmit={requestLink} className="space-y-4" data-testid="reset-request-form">
                <div>
                  <label className="block text-xs font-mono-spec text-slate-700 mb-1">{c.loginEmail}</label>
                  <input
                    data-testid="reset-email"
                    type="email"
                    className={inputClass}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <button
                  data-testid="reset-request-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-mono-spec text-xs font-bold rounded-full uppercase tracking-wider transition-colors disabled:opacity-40"
                >
                  {c.resetSend}
                </button>
              </form>
              <Link to="/konto/login" className="block text-center mt-5 text-xs font-mono-spec text-slate-500 hover:text-slate-950">{c.resetToLogin}</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
